import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import ProductDetails from './ProductDetails';
import Cart from './Cart';
import Checkout from './Checkout';
import OrderHistory from './OrderHistory';
import './ViewAllProducts.css';
import { API } from '../services/apiConfig';

const CAT_MAP = {
  'standard':      'STANDARD',
  'premium':       'PREMIUM',
  'super-premium': 'SUPER_PREMIUM',
};

const CAT_META = {
  STANDARD:      { label: 'Standard',      color: '#107FDF', section: 'standard',      tagline: 'Approachable • Practical • Reliable' },
  PREMIUM:       { label: 'Premium',       color: '#0A683C', section: 'premium',       tagline: 'Refined • Curated • Brand-Focused' },
  SUPER_PREMIUM: { label: 'Super Premium', color: '#9A1111', section: 'super-premium', tagline: 'Exclusive • Experiential • Statement-Making' },
  ALL:           { label: 'All',           color: '#9A1111', section: '',              tagline: 'Explore our full product range' },
};

const COLORS = [
  { name: 'Black', hex: '#111111' }, { name: 'Beige', hex: '#F5F0E8' },
  { name: 'Brown', hex: '#795548' }, { name: 'Blue',  hex: '#2196F3' },
  { name: 'Green', hex: '#4CAF50' }, { name: 'Red',   hex: '#F44336' },
  { name: 'Orange',hex: '#FF9800' }, { name: 'Purple',hex: '#9C27B0' },
  { name: 'Navy',  hex: '#1A237E' }, { name: 'Gray',  hex: '#9E9E9E' },
  { name: 'White', hex: '#FFFFFF' }, { name: 'Pink',  hex: '#E91E63' },
  { name: 'Charcoal', hex: '#36454F' },
];

const SUBCATEGORIES = [
  { value: 'ECO_FRIENDLY',      label: 'Eco Friendly' },
  { value: 'STATIONERY',        label: 'Stationery' },
  { value: 'CORPORATE_ADD_ONS',label: 'Corporate Add-Ons' },
  { value: 'TRAVELLING_ADD_ONS',label: 'Travelling Add-Ons' },
  { value: 'BACKPACKS_AND_SLINGS', label: 'Backpacks & Slings' },
  { value: 'ACCESSORIES',       label: 'Accessories' },
];

const CAT_PRICE = {
  STANDARD:      [0,    1000],
  PREMIUM:       [1000, 5000],
  SUPER_PREMIUM: [5000, 60000],
  ALL:           [0,    60000],
};

function ViewAllProducts({ user, onLogout, onShowAccountDashboard }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params   = new URLSearchParams(location.search);
  const catParam  = params.get('category') || '';
  const qParam    = params.get('q') || '';
  const typeParam = params.get('type') || '';
  const activeCat = catParam ? (CAT_MAP[catParam] || 'STANDARD') : 'ALL';

  const [products, setProducts]     = useState([]);
  const [filtered, setFiltered]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [ratingsMap, setRatingsMap] = useState({});
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [showCart, setShowCart]     = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showOrders, setShowOrders] = useState(false);
  const [headingCat, setHeadingCat] = useState(activeCat);

  const meta = CAT_META[headingCat] || CAT_META[activeCat];

  const [priceRange, setPriceRange] = useState([0, 20000]);
  const [minPrice, setMinPrice]     = useState(0);
  const [maxPrice, setMaxPrice]     = useState(20000);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedSubcats, setSelectedSubcats] = useState([]);
  const [pendingPrice, setPendingPrice]     = useState([0, 20000]);
  const [pendingColors, setPendingColors]   = useState([]);
  const [pendingSubcats, setPendingSubcats] = useState([]);

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const handler = () => { setShowCart(false); setShowCheckout(true); };
    window.addEventListener('openCheckout', handler);
    return () => window.removeEventListener('openCheckout', handler);
  }, []);

  useEffect(() => { setHeadingCat(activeCat); fetchAll(); }, [catParam, qParam, typeParam]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [prodRes, feedRes] = await Promise.all([
        fetch(`${API}/api/products`),
        fetch(`${API}/api/products/feedback/all`),
      ]);
      const data     = prodRes.ok ? await prodRes.json() : [];
      const feedData = feedRes.ok ? await feedRes.json() : [];

      const fixed = Array.isArray(data) ? data.map(p => ({
        ...p,
        mainImage: p.mainImage && !p.mainImage.startsWith('http')
          ? `${API}${p.mainImage}` : p.mainImage,
      })) : [];

      if (Array.isArray(feedData)) {
        const map = {};
        feedData.forEach(f => {
          if (!map[f.productId]) map[f.productId] = [];
          map[f.productId].push(f.rating);
        });
        const ratMap = {};
        Object.entries(map).forEach(([pid, ratings]) => {
          ratMap[Number(pid)] = { avg: ratings.reduce((s, r) => s + r, 0) / ratings.length, count: ratings.length };
        });
        setRatingsMap(ratMap);
      }

      // Filter by category only when no q param (browsing), skip when searching by name
      const catProducts = (activeCat === 'ALL' || qParam) ? fixed : fixed.filter(p => p.category === activeCat);
      // Apply type filter (exact enum match)
      const typeFiltered = typeParam
        ? catProducts.filter(p => p.type === typeParam)
        : catProducts;
      // Apply q text search — exact name match first, then partial
      const preFiltered = qParam
        ? typeFiltered.filter(p =>
            p.name?.toLowerCase() === qParam.toLowerCase() ||
            p.name?.toLowerCase().includes(qParam.toLowerCase()) ||
            p.description?.toLowerCase().includes(qParam.toLowerCase()))
        : typeFiltered;
      const [lo, hi] = CAT_PRICE[activeCat] || [0, 60000];
      setMinPrice(lo); setMaxPrice(hi); setPriceRange([lo, hi]); setPendingPrice([lo, hi]);
      setProducts(preFiltered);
      setFiltered(preFiltered);
      setSelectedColors([]); setSelectedSubcats([]);
      setPendingColors([]); setPendingSubcats([]);
      // Auto-open product details if exact name match found
      if (qParam) {
        const exactMatch = preFiltered.find(p => p.name?.toLowerCase() === qParam.toLowerCase());
        if (exactMatch) {
          setSelectedProductId(exactMatch.id);
          // Use the matched product's actual category for the heading
          setHeadingCat(exactMatch.category || activeCat);
        } else if (preFiltered.length === 1) {
          setSelectedProductId(preFiltered[0].id);
          setHeadingCat(preFiltered[0].category || activeCat);
        } else {
          setHeadingCat(activeCat);
        }
      } else {
        setHeadingCat(activeCat);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const applyFilters = () => {
    setPriceRange(pendingPrice);
    setSelectedColors(pendingColors);
    setSelectedSubcats(pendingSubcats);
    let result = products.filter(p => p.price >= pendingPrice[0] && p.price <= pendingPrice[1]);
    if (pendingColors.length > 0) {
      result = result.filter(p => {
        // Check variants first
        if (p.variants && p.variants.length > 0) {
          return p.variants.some(v => pendingColors.some(c => v.color?.toUpperCase() === c.toUpperCase()));
        }
        // For products without variants, search color in name, description, details and specifications
        const searchText = [
          p.name || '',
          p.description || '',
          p.details || '',
          Object.values(p.specifications || {}).join(' '),
          Object.keys(p.specifications || {}).join(' ')
        ].join(' ').toLowerCase();
        return pendingColors.some(c => searchText.includes(c.toLowerCase()));
      });
    }
    if (pendingSubcats.length > 0) {
      result = result.filter(p => pendingSubcats.includes(p.type));
    }
    setFiltered(result);
  };

  const otherCats = Object.entries(CAT_META).filter(([k]) => k !== activeCat && k !== 'ALL');

  return (
    <div id="page" className="page">
      <Header
        onContactClick={() => {}} onLoginClick={() => {}}
        onCartClick={() => setShowCart(true)}
        onShowOrders={() => setShowOrders(true)}
        onShowAccountDashboard={onShowAccountDashboard}
        user={user} onLogout={onLogout}
        onProductClick={(p) => setSelectedProductId(p.id)}
      />

      {/* Category heading */}
      <div className="vap-cat-heading">
        <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#111', margin: '0 0 6px', lineHeight: 1.2 }}>
          {meta.label} <span style={{ color: meta.color }}>Products</span>
        </h2>
        <p style={{ fontSize: '14px', color: '#777', margin: '0 0 0', fontStyle: 'italic' }}>{meta.tagline}</p>
        <div style={{ width: '48px', height: '3px', background: meta.color, borderRadius: '2px', marginTop: '14px' }} />
      </div>

      <div className="vap-wrapper">
        <div className="vap-inner">

          {/* Mobile filter toggle */}
          <button className="vap-filter-toggle-btn" onClick={() => setShowFilters(f => !f)}>
            <i className="fas fa-sliders-h"></i>
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>

          {/* Sidebar */}
          <aside className={`vap-sidebar${showFilters ? ' open' : ''}`}>
            <div className="vap-filter-block">
              <h4 className="vap-filter-title">Set Budget</h4>
              <div className="vap-range-labels">
                <span>Min: ₹{pendingPrice[0].toLocaleString()}</span>
                <span>Max: ₹{pendingPrice[1].toLocaleString()}</span>
              </div>
              <div className="vap-range-wrap">
                <input type="range" min={minPrice} max={maxPrice} value={pendingPrice[0]}
                  onChange={e => setPendingPrice([Math.min(Number(e.target.value), pendingPrice[1] - 1), pendingPrice[1]])}
                  className="vap-range vap-range-min" />
                <input type="range" min={minPrice} max={maxPrice} value={pendingPrice[1]}
                  onChange={e => setPendingPrice([pendingPrice[0], Math.max(Number(e.target.value), pendingPrice[0] + 1)])}
                  className="vap-range vap-range-max" />
              </div>
            </div>

            <div className="vap-filter-block">
              <h4 className="vap-filter-title">Subcategory</h4>
              <div className="vap-subcat-list">
                {SUBCATEGORIES.map(s => (
                  <label key={s.value} className="vap-color-row">
                    <input type="checkbox" className="vap-checkbox"
                      checked={pendingSubcats.includes(s.value)}
                      onChange={() => setPendingSubcats(prev =>
                        prev.includes(s.value) ? prev.filter(x => x !== s.value) : [...prev, s.value]
                      )} />
                    <span className="vap-color-label" style={{ fontSize: '12px' }}>{s.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="vap-filter-block">
              <div className="vap-color-grid">
                {COLORS.map(c => (
                  <label key={c.name} className="vap-color-row">
                    <input type="checkbox" checked={pendingColors.includes(c.name)}
                      onChange={() => setPendingColors(prev => prev.includes(c.name) ? prev.filter(x => x !== c.name) : [...prev, c.name])}
                      className="vap-checkbox" />
                    <span className="vap-color-dot" style={{ background: c.hex }} />
                    <span className="vap-color-label">{c.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <button className="vap-apply-btn" style={{ background: meta.color }} onClick={applyFilters}>Apply Filters</button>
          </aside>

          {/* Product Grid */}
          <div className="vap-grid-area">
            {loading ? (
              <div className="vap-empty">Loading products...</div>
            ) : filtered.length === 0 ? (
              <div className="vap-empty">
                <p style={{ fontSize: '15px', color: '#666', marginBottom: '8px' }}>
                  No products found in the <strong>{meta.label}</strong> category for this selection.
                </p>
                <p style={{ fontSize: '13px', color: '#888' }}>
                  Please explore our{' '}
                  {activeCat !== 'STANDARD' && <a href="/products?category=standard" style={{ color: '#107FDF', fontWeight: '600', textDecoration: 'underline' }}>Standard</a>}
                  {activeCat !== 'STANDARD' && activeCat !== 'PREMIUM' && ' or '}
                  {activeCat !== 'PREMIUM' && <a href="/products?category=premium" style={{ color: '#0A683C', fontWeight: '600', textDecoration: 'underline' }}>Premium</a>}
                  {activeCat !== 'SUPER_PREMIUM' && ' or '}
                  {activeCat !== 'SUPER_PREMIUM' && <a href="/products?category=super-premium" style={{ color: '#9A1111', fontWeight: '600', textDecoration: 'underline' }}>Super Premium</a>}
                  {' '}range for more suitable options.
                </p>
              </div>
            ) : (
              <>
                <div className="vap-grid">
                  {filtered.map(p => (
                    <div key={p.id} className="vap-card" onClick={() => setSelectedProductId(p.id)}>
                      <div className="vap-card-tag-wrap">
                        <div className="vap-card-tag" style={{ color: meta.color }}>
                          {p.type?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) || 'Product'}
                        </div>
                      </div>
                      <div className="vap-card-img">
                        <img src={p.mainImage || '/images/product.jpg'} alt={p.name}
                          onError={e => { e.target.src = '/images/product.jpg'; }} />
                      </div>
                      <p className="vap-card-name">{p.name}</p>
                      <div style={{ display: 'flex', gap: '2px', margin: '4px 0' }}>
                        {[...Array(5)].map((_, i) => (
                          <span key={i} style={{ fontSize: '13px', color: ratingsMap[p.id] && i < Math.round(ratingsMap[p.id].avg) ? '#ffc107' : '#ddd' }}>★</span>
                        ))}
                      </div>
                      <div className="vap-card-bottom">
                        <span className="vap-card-price" style={{ color: meta.color }}>₹{p.price?.toLocaleString('en-IN')}</span>
                        <button className="vap-card-btn" style={{ color: meta.color }}
                          onClick={e => { e.stopPropagation(); setSelectedProductId(p.id); }}>View</button>
                      </div>
                    </div>
                  ))}
                </div>


              </>
            )}
          </div>
        </div>
      </div>

      {/* Category suggestion banners — aligned with footer */}
      <div style={{ maxWidth: '1200px', margin: '0 auto 0', padding: '0 40px 40px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {otherCats.map(([key, m]) => {
            const bannerIdx = key === 'STANDARD' ? 0 : key === 'PREMIUM' ? 1 : 2;
            const bannerImgs = ['/images/hero%20banner%201.png', '/images/hero%20banner%202.png', '/images/hero%20banner%203.png'];
            return (
              <div key={key} onClick={() => navigate(`/products?category=${m.section}`)}
                className="vap-cat-banner">
                <img src={bannerImgs[bannerIdx]} alt={m.label}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', position: 'absolute', inset: 0 }} />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.2) 70%, transparent 100%)' }} />
                <div className="vap-cat-banner-content">
                  <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', margin: '0 0 6px' }}>Explore</p>
                  <h3 style={{ color: '#fff', fontWeight: '800', margin: '0 0 6px', lineHeight: 1.2 }}>{m.label}</h3>
                  <p style={{ color: 'rgba(255,255,255,0.8)', margin: '0 0 12px' }}>{m.tagline}</p>
                  <span style={{ display: 'inline-block', background: '#fff', color: m.color, borderRadius: '20px', padding: '5px 16px', fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap', width: '89px' }}>View All →</span>
                </div>
              </div>
            );
          })}
          <div onClick={() => navigate('/')}
            className="vap-cat-banner vap-cat-banner-full">
            <img src="/images/hero%20banner%204.png" alt="All Products"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', position: 'absolute', inset: 0 }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.58) 0%, rgba(0,0,0,0.15) 60%, transparent 100%)' }} />
            <div className="vap-cat-banner-content">
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', margin: '0 0 6px' }}>corgif</p>
              <h3 style={{ color: '#fff', fontWeight: '800', margin: '0 0 6px' }}>Gift Beyond Expectations.</h3>
              <p style={{ color: 'rgba(255,255,255,0.8)', margin: '0 0 14px' }}>Elevate your brand with every box.</p>
              <span style={{ display: 'inline-block', background: '#9A1111', color: '#fff', borderRadius: '20px', padding: '6px 20px', fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap', width: '129px' }}>Back to Home →</span>
            </div>
          </div>
        </div>
      </div>

          <Footer />
      {selectedProductId && (
        <ProductDetails productId={selectedProductId} onClose={() => {
          setSelectedProductId(null);
          if (qParam) {
            const cat = CAT_META[headingCat]?.section || 'standard';
            navigate(`/products?category=${cat}`, { replace: true });
          }
        }}
          onProductClick={setSelectedProductId} onGoToCart={() => { setSelectedProductId(null); setShowCart(true); }} />
      )}
      {showCart && <Cart onClose={() => setShowCart(false)} />}
      {showCheckout && <Checkout user={user} onClose={() => setShowCheckout(false)} onOrderPlaced={() => { setTimeout(() => setShowCheckout(false), 3000); }} />}
      {showOrders && <OrderHistory userId={user?.userId} onClose={() => setShowOrders(false)} />}
    </div>
  );
}

export default ViewAllProducts;
